import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { TruncatedText } from "@/components/ui/truncated-text";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { SlidersHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FilterColumn } from "@/components/shared/FilterDropdown";
import type { RangeFilterColumn, RangeFilterState } from "@/components/keyword-bank/constants";

export interface TextFilterColumn {
  id: string;
  label: string;
  placeholder?: string;
  hint?: string;
  chipPrefix?: string;
  // AUT-419: when set, renders an Any/All match-mode toggle under the input, keyed here in `matchModes`.
  matchModeKey?: string;
}

interface CombinedFilterDropdownProps {
  columns: FilterColumn[];
  activeFilters: Record<string, string[]>;
  onFiltersChange: (filters: Record<string, string[]>) => void;
  rangeColumns: RangeFilterColumn[];
  activeRangeFilters: RangeFilterState;
  onRangeFiltersChange: (filters: RangeFilterState) => void;
  dataRanges?: Record<string, { min: number; max: number }>;
  // AUT-419: optional free-text filters (e.g. "Phrases Containing" / "Exclude Keywords"). Comma-
  // separated raw strings; the consumer parses them into terms.
  textColumns?: TextFilterColumn[];
  activeTextFilters?: Record<string, string>;
  onTextFiltersChange?: (filters: Record<string, string>) => void;
  matchModes?: Record<string, string>;
  onMatchModesChange?: (modes: Record<string, string>) => void;
  /**
   * AUT-478: how many grid columns the checkbox section lays out across. Defaults to one column
   * per filter (capped at 4), which is what every caller before this got. Pass a fixed number to
   * keep a short checkbox list aligned with the range grid below instead of letting one or two
   * filters stretch across the whole panel.
   */
  checkboxPanelColumns?: number;
  className?: string;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// AUT-512: one chip for all three kinds. The label truncates (TruncatedText shows the full text in a
// tooltip when clipped) so a long unbroken value cannot stretch the toolbar; × stays visible via `shrink-0`.
function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium border border-primary/20">
      <TruncatedText className="max-w-[20rem]">{label}</TruncatedText>
      <button
        onClick={onRemove}
        className="shrink-0 hover:bg-primary/20 rounded-full p-0.5 transition-colors"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}

export function CombinedFilterDropdown({
  columns,
  activeFilters,
  onFiltersChange,
  rangeColumns,
  activeRangeFilters,
  onRangeFiltersChange,
  dataRanges,
  textColumns = [],
  activeTextFilters = {},
  onTextFiltersChange,
  matchModes = {},
  onMatchModesChange,
  checkboxPanelColumns,
  className,
}: CombinedFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // AUT-511: px offset (<= 0) that pulls the panel back inside the viewport.
  const [panelShift, setPanelShift] = useState(0);
  // AUT-515: bounds whose number input holds unparseable text (`validity.badInput`, e.g. "20000k" in
  // Firefox or "0." in Chrome). The ref is current for the debounced effect; the state drives the
  // error UI, which is shown only from its debounced copy so a transient "0." / "1e" does not flash.
  type BadFlags = Record<string, { min?: boolean; max?: boolean }>;
  const badRef = useRef<BadFlags>({});
  const [badInputs, setBadInputs] = useState<BadFlags>({});
  const debouncedBad = useDebounce(badInputs, 300);
  const writeBad = useCallback((next: BadFlags) => {
    badRef.current = next;
    setBadInputs(next);
  }, []);

  // AUT-419: local text-filter inputs, debounced so a 2k-row client filter doesn't run per keystroke.
  const [localText, setLocalText] = useState<Record<string, string>>(() => ({ ...activeTextFilters }));
  const debouncedText = useDebounce(localText, 300);
  const onTextFiltersChangeRef = useRef(onTextFiltersChange);
  onTextFiltersChangeRef.current = onTextFiltersChange;
  const activeTextFiltersRef = useRef(activeTextFilters);
  activeTextFiltersRef.current = activeTextFilters;

  useEffect(() => {
    onTextFiltersChangeRef.current?.(debouncedText);
  }, [debouncedText]);

  const [localInputs, setLocalInputs] = useState<Record<string, { min: string; max: string }>>(() => {
    const init: Record<string, { min: string; max: string }> = {};
    rangeColumns.forEach((col) => {
      const active = activeRangeFilters[col.id];
      init[col.id] = {
        min: active?.min != null ? String(active.min) : "",
        max: active?.max != null ? String(active.max) : "",
      };
    });
    return init;
  });

  const debouncedInputs = useDebounce(localInputs, 300);

  const activeRangeFiltersRef = useRef(activeRangeFilters);
  activeRangeFiltersRef.current = activeRangeFilters;

  const rangeColumnsRef = useRef(rangeColumns);
  rangeColumnsRef.current = rangeColumns;

  const onRangeFiltersChangeRef = useRef(onRangeFiltersChange);
  onRangeFiltersChangeRef.current = onRangeFiltersChange;

  useEffect(() => {
    const cols = rangeColumnsRef.current;
    const next: RangeFilterState = { ...activeRangeFiltersRef.current };
    cols.forEach((col) => {
      const raw = debouncedInputs[col.id];
      if (!raw) return;
      const minNum = raw.min !== "" ? Number(raw.min) : null;
      const maxNum = raw.max !== "" ? Number(raw.max) : null;
      // AUT-515: a bound whose input is bad (state holds "") keeps the value currently applied.
      const applied = activeRangeFiltersRef.current[col.id];
      const bad = badRef.current[col.id];
      const min = bad?.min ? (applied?.min ?? null) : minNum != null && Number.isFinite(minNum) ? minNum : null;
      const max = bad?.max ? (applied?.max ?? null) : maxNum != null && Number.isFinite(maxNum) ? maxNum : null;
      if (min != null && max != null && min > max) {
        next[col.id] = { min: null, max: null };
        return;
      }
      next[col.id] = { min, max };
    });
    onRangeFiltersChangeRef.current(next);
  }, [debouncedInputs]);

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      const cols = rangeColumnsRef.current;
      const active = activeRangeFiltersRef.current;
      const newLocal: Record<string, { min: string; max: string }> = {};
      cols.forEach((col) => {
        const f = active[col.id];
        newLocal[col.id] = {
          min: f?.min != null ? String(f.min) : "",
          max: f?.max != null ? String(f.max) : "",
        };
      });
      setLocalInputs(newLocal);
      writeBad({});
      setLocalText({ ...activeTextFiltersRef.current });
    }
    prevOpenRef.current = open;
  }, [open]);

  // AUT-511: the panel is `absolute left-0` of the trigger, which can sit far from the viewport's left
  // edge. Measure on open and on resize, and shift left just enough to keep the right edge in view
  // (never past 16px from the viewport's left). Offset stays 0 when it already fits.
  useLayoutEffect(() => {
    if (!open) {
      setPanelShift(0);
      return;
    }
    function reposition() {
      if (!ref.current) return;
      const margin = 16;
      const wrapperLeft = ref.current.getBoundingClientRect().left;
      const width = panelRef.current?.offsetWidth || Math.min(920, window.innerWidth - 2 * margin);
      let shift = Math.min(0, window.innerWidth - margin - (wrapperLeft + width));
      shift = Math.min(0, Math.max(shift, margin - wrapperLeft));
      setPanelShift(shift);
    }
    reposition();
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [open]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  const checkboxActiveCount = Object.values(activeFilters).reduce(
    (sum, arr) => sum + arr.length,
    0,
  );

  const rangeActiveCount = rangeColumns.filter((col) => {
    const f = activeRangeFilters[col.id];
    return f && (f.min != null || f.max != null);
  }).length;

  const textActiveCount = textColumns.filter((col) => (activeTextFilters[col.id] ?? "").trim() !== "").length;

  const totalActiveCount = checkboxActiveCount + rangeActiveCount + textActiveCount;

  const toggleValue = (columnId: string, value: string) => {
    const current = activeFilters[columnId] || [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onFiltersChange({ ...activeFilters, [columnId]: next });
  };

  const clearAll = useCallback(() => {
    const clearedCheckbox: Record<string, string[]> = {};
    columns.forEach((col) => {
      clearedCheckbox[col.id] = [];
    });
    onFiltersChange(clearedCheckbox);

    const clearedRange: RangeFilterState = {};
    const clearedLocal: Record<string, { min: string; max: string }> = {};
    rangeColumnsRef.current.forEach((col) => {
      clearedRange[col.id] = { min: null, max: null };
      clearedLocal[col.id] = { min: "", max: "" };
    });
    setLocalInputs(clearedLocal);
    writeBad({});
    onRangeFiltersChangeRef.current(clearedRange);

    const clearedText: Record<string, string> = {};
    textColumns.forEach((col) => { clearedText[col.id] = ""; });
    setLocalText(clearedText);
    onTextFiltersChangeRef.current?.(clearedText);
  }, [columns, onFiltersChange, textColumns]);

  const removeCheckboxChip = (columnId: string, value: string) => {
    const current = activeFilters[columnId] || [];
    onFiltersChange({ ...activeFilters, [columnId]: current.filter((v) => v !== value) });
  };

  const removeRangeChip = useCallback((colId: string) => {
    setLocalInputs((prev) => ({ ...prev, [colId]: { min: "", max: "" } }));
    writeBad({ ...badRef.current, [colId]: {} });
    onRangeFiltersChangeRef.current({
      ...activeRangeFiltersRef.current,
      [colId]: { min: null, max: null },
    });
  }, []);

  const handleRangeInputChange = (colId: string, bound: "min" | "max", target: HTMLInputElement) => {
    // AUT-515: a number input holding unparseable text reports value "" with badInput set. Flag it and
    // put "" in state (equal to node.value, so React's controlled-input restore leaves the user's
    // text alone); the debounced effect keeps the last applied bound while the flag is set. Always a
    // fresh localInputs object so the debounce re-runs even for "" -> "" (bad -> cleared field).
    const bad = !!target.validity?.badInput;
    writeBad({ ...badRef.current, [colId]: { ...badRef.current[colId], [bound]: bad } });
    if (bad) {
      setLocalInputs((prev) => ({ ...prev, [colId]: { ...prev[colId], [bound]: "" } }));
      return;
    }
    let next = target.value;
    const col = rangeColumns.find((c) => c.id === colId);
    if (next !== "" && col && (col.minBound != null || col.maxBound != null)) {
      const n = Number(next);
      if (Number.isFinite(n)) {
        let clamped = n;
        if (col.minBound != null) clamped = Math.max(col.minBound, clamped);
        if (col.maxBound != null) clamped = Math.min(col.maxBound, clamped);
        next = String(clamped);
      }
    }
    setLocalInputs((prev) => ({
      ...prev,
      [colId]: { ...prev[colId], [bound]: next },
    }));
  };

  const checkboxChips: { columnId: string; value: string; label: string }[] = [];
  columns.forEach((col) => {
    (activeFilters[col.id] || []).forEach((val) => {
      const opt = col.options.find((o) => o.value === val);
      checkboxChips.push({ columnId: col.id, value: val, label: opt?.label || val });
    });
  });

  const rangeChips = rangeColumns
    .filter((col) => {
      const f = activeRangeFilters[col.id];
      return f && (f.min != null || f.max != null);
    })
    .map((col) => {
      const f = activeRangeFilters[col.id];
      const minStr = f?.min != null ? f.min.toLocaleString() : null;
      const maxStr = f?.max != null ? f.max.toLocaleString() : null;
      let label = col.label + ": ";
      if (minStr != null && maxStr != null) {
        label += `${minStr} - ${maxStr}`;
      } else if (minStr != null) {
        label += `>= ${minStr}`;
      } else {
        label += `<= ${maxStr}`;
      }
      return { colId: col.id, label };
    });

  const textChips = textColumns
    .filter((col) => (activeTextFilters[col.id] ?? "").trim() !== "")
    .map((col) => ({
      colId: col.id,
      label: `${col.chipPrefix ?? col.label}: ${(activeTextFilters[col.id] ?? "").trim()}`,
    }));

  const removeTextChip = (colId: string) => {
    setLocalText((prev) => ({ ...prev, [colId]: "" }));
    onTextFiltersChangeRef.current?.({ ...activeTextFiltersRef.current, [colId]: "" });
  };

  const hasChips = checkboxChips.length > 0 || rangeChips.length > 0 || textChips.length > 0;

  return (
    <>
      <div className="relative" ref={ref}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(!open)}
          className={cn(
            "h-9 gap-1 rounded-[10px] bg-card",
            open && "ring-2 ring-primary/20",
            totalActiveCount > 0 && "border-primary/50",
          )}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {totalActiveCount > 0 && (
            <span className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">
              {totalActiveCount}
            </span>
          )}
        </Button>

        {open && (
          <div
            ref={panelRef}
            style={panelShift !== 0 ? { left: panelShift } : undefined}
            className={cn(
              // AUT-419: wider, roomier panel closer to the prototype's full-width filter design (kept
              // as a safe dropdown rather than the prototype's fragile table-width JS alignment).
              "absolute top-[calc(100%+10px)] left-0 z-50 bg-card border border-border rounded-[10px] shadow-lg animate-in fade-in-0 zoom-in-95 duration-150 flex flex-col max-h-[70vh] w-[min(920px,calc(100vw-2rem))]",
              className,
            )}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-4 shrink-0">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Filters
              </span>
              <div className="flex items-center gap-2">
                {totalActiveCount > 0 && (
                  <button
                    onClick={clearAll}
                    className="text-xs text-primary hover:text-primary/80 font-semibold transition-colors"
                  >
                    Clear all
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto px-5 pb-5">
            <div
              className="grid gap-x-6 gap-y-4"
              style={{
                gridTemplateColumns: `repeat(${checkboxPanelColumns ?? Math.min(columns.length, 4)}, 1fr)`,
              }}
            >
              {columns.map((col) => (
                <div
                  key={col.id}
                  className="min-w-0"
                  style={col.columnSpan ? { gridColumn: `span ${col.columnSpan}` } : undefined}
                >
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    {col.label}
                  </p>
                  <div className="space-y-1">
                    {col.options.map((opt) => {
                      const isChecked = (activeFilters[col.id] || []).includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className={cn(
                            "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm transition-colors",
                            isChecked
                              ? "bg-primary/10 text-foreground"
                              : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                          )}
                        >
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() => toggleValue(col.id, opt.value)}
                          />
                          <span className="text-sm">{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {textColumns.length > 0 && (
              <>
                <div className="border-t border-border my-4" />
                <div className="grid grid-cols-2 gap-4">
                  {textColumns.map((col) => (
                    <div key={col.id}>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        {col.label}
                      </p>
                      <Input
                        type="text"
                        placeholder={col.placeholder ?? ""}
                        value={localText[col.id] ?? ""}
                        onChange={(e) => setLocalText((prev) => ({ ...prev, [col.id]: e.target.value }))}
                        className="h-8 text-sm"
                      />
                      {col.matchModeKey && (
                        // AUT-419: Any/All match mode (matches the Helium10 reference + prototype).
                        <RadioGroup
                          className="flex items-center gap-4 mt-2"
                          value={matchModes[col.matchModeKey] ?? "any"}
                          onValueChange={(v) => onMatchModesChange?.({ ...matchModes, [col.matchModeKey!]: v })}
                        >
                          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                            <RadioGroupItem value="any" /> Any
                          </label>
                          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                            <RadioGroupItem value="all" /> All
                          </label>
                        </RadioGroup>
                      )}
                      {col.hint && <p className="text-[11px] text-muted-foreground mt-1">{col.hint}</p>}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="border-t border-border my-4" />

            <div className="grid grid-cols-4 gap-4">
              {rangeColumns.map((col) => {
                const range = dataRanges?.[col.id];
                const rawLocal = localInputs[col.id];
                const minNum = rawLocal?.min ? Number(rawLocal.min) : null;
                const maxNum = rawLocal?.max ? Number(rawLocal.max) : null;
                // AUT-515: both copies must be set: debounced (no flash of a transient "0.") and current (a reset hides it at once).
                const badMin = !!debouncedBad[col.id]?.min && !!badInputs[col.id]?.min;
                const badMax = !!debouncedBad[col.id]?.max && !!badInputs[col.id]?.max;
                const invalid =
                  minNum != null && maxNum != null &&
                  Number.isFinite(minNum) && Number.isFinite(maxNum) &&
                  minNum > maxNum;
                return (
                  <div key={col.id}>
                    <div className="mb-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {col.label}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">Min</span>
                        <Input
                          type="number"
                          placeholder={range != null ? range.min.toLocaleString() : "Min"}
                          value={localInputs[col.id]?.min ?? ""}
                          onChange={(e) => handleRangeInputChange(col.id, "min", e.target)}
                          // AUT-515: React dedups onChange when value stays "" (abc typed into an empty field).
                          onInput={(e) => handleRangeInputChange(col.id, "min", e.currentTarget)}
                          min={col.minBound}
                          max={col.maxBound}
                          aria-invalid={invalid || badMin}
                          className={cn("h-8 text-sm", (invalid || badMin) && "border-status-error-mid focus-visible:ring-status-error-mid")}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">Max</span>
                        <Input
                          type="number"
                          placeholder={range != null ? range.max.toLocaleString() : "Max"}
                          value={localInputs[col.id]?.max ?? ""}
                          onChange={(e) => handleRangeInputChange(col.id, "max", e.target)}
                          // AUT-515: React dedups onChange when value stays "" (abc typed into an empty field).
                          onInput={(e) => handleRangeInputChange(col.id, "max", e.currentTarget)}
                          min={col.minBound}
                          max={col.maxBound}
                          aria-invalid={invalid || badMax}
                          className={cn("h-8 text-sm", (invalid || badMax) && "border-status-error-mid focus-visible:ring-status-error-mid")}
                        />
                      </div>
                      {(badMin || badMax) && (
                        <p className="text-xs text-status-error-strong">Enter a number</p>
                      )}
                      {invalid && (
                        <p className="text-xs text-status-error-strong">Minimum value cannot exceed maximum value</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        )}
      </div>

      {hasChips && (
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          {checkboxChips.map((chip) => (
            <FilterChip
              key={`${chip.columnId}-${chip.value}`}
              label={chip.label}
              onRemove={() => removeCheckboxChip(chip.columnId, chip.value)}
            />
          ))}
          {rangeChips.map((chip) => (
            <FilterChip key={chip.colId} label={chip.label} onRemove={() => removeRangeChip(chip.colId)} />
          ))}
          {textChips.map((chip) => (
            <FilterChip key={chip.colId} label={chip.label} onRemove={() => removeTextChip(chip.colId)} />
          ))}
          <button
            onClick={clearAll}
            className="text-xs text-primary hover:text-primary/80 font-semibold transition-colors ml-1"
          >
            Clear all
          </button>
        </div>
      )}
    </>
  );
}
